import { addNegativeToast, removeAllToasts } from 'modules/toastContainer';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { SecondaryButton } from 'ui/button';
import { Add16 } from 'ui/icons';
import {
  ACCEPTS_TYPES,
  MAX_IMAGE_SIZE,
  MIN_IMAGE_SIZE,
  PROJECT_FIELD_NAMES,
} from '../../constants';
import { ProjectSettingsFormSchemaType, useProjectSettingsQueryContext } from '../../contexts';
import { ConnectedFormField } from './ConnectedFormField';
import {
  Details,
  FileInput,
  FileName,
  LogoField,
  LogoFieldWrapper,
  ReplaceButton,
  Thumbnail,
} from './LogoImageField.styled';

const { LOGO_URL } = PROJECT_FIELD_NAMES;

export const LogoImageField = () => {
  const {
    project: { logoUrl = '' },
  } = useProjectSettingsQueryContext();
  const { watch, setValue } = useFormContext<ProjectSettingsFormSchemaType>();
  const dispatch = useDispatch();

  const file = watch(LOGO_URL);

  const onDropAccepted = (files: File[]) => {
    dispatch(removeAllToasts());
    const [newFile] = files;
    setValue(LOGO_URL, newFile, { shouldDirty: true });
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: ACCEPTS_TYPES,
    maxSize: MAX_IMAGE_SIZE,
    minSize: MIN_IMAGE_SIZE,
    multiple: false,
    onDropAccepted,
    onDropRejected: () => {
      dispatch(addNegativeToast('Invalid file type or size. Please upload a file under 3MB.'));
    },
  });

  let fileText = '';
  if (file) {
    fileText = (file as File).name;
  } else if (logoUrl) {
    fileText = logoUrl;
  }
  const imgSrc = file instanceof File ? URL.createObjectURL(file) : logoUrl;
  const rootProps = getRootProps();
  const inputProps = getInputProps();

  return (
    <>
      {!logoUrl && !fileText ? (
        <ConnectedFormField name={LOGO_URL}>
          <LogoFieldWrapper {...rootProps}>
            <SecondaryButton type="button">
              <Add16 />
              <span>Add a logo</span>
            </SecondaryButton>
            <FileInput {...inputProps} hidden multiple={false} type="file" />
          </LogoFieldWrapper>
        </ConnectedFormField>
      ) : (
        <LogoField>
          <Thumbnail src={imgSrc} alt="Logo Image" />
          <Details>
            {logoUrl && !fileText && <FileName>{logoUrl}</FileName>}
            {fileText && <FileName>{fileText}</FileName>}
          </Details>
          <LogoFieldWrapper {...rootProps}>
            <ReplaceButton type="button" data-test="replace-logo-button">
              Replace logo
            </ReplaceButton>
            <FileInput {...inputProps} hidden multiple={false} type="file" />
          </LogoFieldWrapper>
        </LogoField>
      )}
    </>
  );
};
